import { css, cx } from "emotion";
import PropTypes from "prop-types";
import { includes, prop } from "ramda";
import React from "react";
import { Link } from "react-router-dom";
import {
  Loading,
  DropdownMenu,
  Receipt as OrderReceipt,
} from "../../../components";
import { isAbsent, isPresent } from "../../../utils";
import RefundModal from "./RefundModal";
import SuccessModal from "./SuccessModal";

const style = getStyle();
const print = () => window.print();

const optionRenderer = ({ label, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className={disabled ? "disabled-option" : ""}
  >
    {label}
  </button>
);

optionRenderer.propTypes = {
  label: PropTypes.any,
  onClick: PropTypes.any,
  disabled: PropTypes.bool,
};

const DropdownLabel = ({ selectedItems, totalItems }) => {
  const totalSelectedItems = selectedItems.length;

  if (totalSelectedItems > 0 && totalSelectedItems === totalItems) {
    return (
      <div aria-label={`Full refund selected - ${selectedItems.length} items`}>
        Full Refund ({selectedItems.length})
      </div>
    );
  }

  if (totalSelectedItems) {
    return (
      <div
        aria-label={`Partial refund selected - ${selectedItems.length} items`}
      >
        Partial Refund ({selectedItems.length})
      </div>
    );
  }

  return <div aria-label="Print receipt option">Print Receipt</div>;
};

DropdownLabel.propTypes = {
  selectedItems: PropTypes.any,
  totalItems: PropTypes.any,
};

const View = (props) => {
  const { selectedItems = [], itemIds = [] } = props;

  const getStatusMessage = () => {
    if (props.isLoading && !props.hasOrder) {
      return "Loading order details...";
    }
    if (props.error) {
      return "Error loading order. Please try again.";
    }
    if (props.hasOrder) {
      return `Order loaded successfully. ${selectedItems.length} items selected.`;
    }
    return "";
  };

  const statusMessage = getStatusMessage();

  return (
    <div
      className={cx(props.className, style.main, style.print)}
      role="main"
      aria-label="Order receipt view"
    >
      <RefundModal {...props.refund} selectedItems={selectedItems} />
      <SuccessModal {...props.refund} />

      {statusMessage && (
        <div
          className="sr-only"
          aria-live="polite"
          aria-atomic="true"
          aria-label="Order status"
        >
          {statusMessage}
        </div>
      )}

      <header className="order-receipt-header" role="banner">
        <nav role="navigation" aria-label="Breadcrumb navigation">
          <Link
            to={`/${props.countyId}/dashboard`}
            className="order-receipt-header--title"
            aria-label="Return to dashboard"
          >
            ← Back to Dashboard
          </Link>
        </nav>

        <div role="toolbar" aria-label="Order actions">
          <DropdownMenu
            className="receipt-dropdown"
            label={
              <DropdownLabel
                selectedItems={selectedItems}
                totalItems={itemIds.length}
              />
            }
            options={getOptions(props)}
            optionRenderer={optionRenderer}
            data-testid="selectionDropdown"
            aria-label="Order action menu"
            aria-describedby="dropdown-description"
          />
          <div id="dropdown-description" className="sr-only">
            Menu for printing receipt, refunding order, or downloading documents
          </div>
        </div>
      </header>

      <main role="region" aria-label="Order content">
        {props.isLoading && !props.hasOrder && (
          <div role="status" aria-label="Loading order">
            <Loading />
          </div>
        )}

        {props.error && (
          <div
            role="alert"
            aria-live="assertive"
            className="error-message"
            aria-label="Error loading order"
          >
            <span className="error-icon" aria-hidden="true">
              ⚠️
            </span>
            {isPresent(prop("message", props.error))
              ? prop("message", props.error)
              : "There&apos;s been an error getting this order. Please try again."}
          </div>
        )}

        {props.hasOrder && isAbsent(props.error) && (
          <section
            role="region"
            aria-label="Order receipt"
            aria-describedby="receipt-description"
          >
            <div id="receipt-description" className="sr-only">
              Detailed order receipt with line items and totals
            </div>
            <OrderReceipt
              className="order-receipt-table"
              {...props.order}
              nonInteractive={false}
            />
          </section>
        )}
      </main>
    </div>
  );
};

View.displayName = "Order View";

View.propTypes = {
  order: PropTypes.any,
  hasOrder: PropTypes.bool,
  isLoading: PropTypes.bool,
  countyId: PropTypes.any,
  className: PropTypes.any,
  selectedItems: PropTypes.array.isRequired,
  itemIds: PropTypes.array.isRequired,
  refund: PropTypes.shape({
    modal: PropTypes.shape({
      open: PropTypes.func.isRequired,
      close: PropTypes.func.isRequired,
      show: PropTypes.bool.isRequired,
    }).isRequired,
    order: PropTypes.object,
  }),
};

View.defaultProps = {
  selectedItems: [],
  itemIds: [],
};

export default View;

const RefundType = {
  partial: "PARTIAL_REFUND",
  full: "FULL_REFUND",
};

function getOptions(props) {
  if (!props.order) return [];

  const { selectedItems = [], itemIds = [], order, refundQuoteItems } = props;
  const numSelectedItems = selectedItems.length;
  const totalItems = itemIds.length;
  const canPartialRefund = numSelectedItems && numSelectedItems !== totalItems;

  const determineStatus = ({ refundStatus }) =>
    refundStatus === "none" || "completed-rejected";

  const canFullRefund =
    order.lineItems.every(determineStatus) && !canPartialRefund;

  const fetchRefundTotal = (type) => () => {
    const refundItems =
      type === RefundType.full
        ? refundQuoteItems
        : refundQuoteItems.filter((item) => includes(item.id, selectedItems));

    props.fetchRefundQuote({ orderId: order.orderId, refundItems });
    props.refund.modal.open();
  };

  const options = [
    {
      label: "Print Receipt",
      onClick: print,
      disabled: false,
      ariaLabel: "Print current order receipt",
    },
    {
      label: "Email Receipt",
      onClick: () => {},
      disabled: true,
      ariaLabel: "Email receipt (currently unavailable)",
    },
    {
      label: "Refund Order",
      onClick: fetchRefundTotal(RefundType.full),
      disabled: !canFullRefund,
      ariaLabel: canFullRefund
        ? "Process full refund for entire order"
        : "Full refund not available",
    },
    {
      label: `Partial Refund (${numSelectedItems})`,
      onClick: fetchRefundTotal(RefundType.partial),
      disabled: !canPartialRefund,
      ariaLabel: canPartialRefund
        ? `Process partial refund for ${numSelectedItems} selected items`
        : "Partial refund not available",
    },
    {
      label: canPartialRefund
        ? `Download Documents (${numSelectedItems})`
        : "Download Document",
      onClick: () => {},
      disabled: true,
      ariaLabel: "Download documents (currently unavailable)",
    },
  ];

  return options.filter(({ disabled }) => disabled === false);
}

function getStyle() {
  return {
    main: css`
      margin: 32px auto;
      max-width: 960px;
      padding: 0 16px;
      font-size: 14px;
    `,
    print: css`
      @media print {
        @page {
          margin: 0;
        }

        margin: 0;
        max-width: none;
        padding: 10px;
        width: 100%;
      }
    `,
  };
}
